// A small nonlinear head over the linear evaluation.
//
// Every other weight in `tuning.ts` is a coefficient: the score is a weighted
// sum, so no weight can express "material matters *less* once the factory is
// running" or "track is worth more when there is ore to put on it". Those are
// interactions between terms, and a linear model has none by construction.
// This is the smallest thing that does: a four-layer narrowing perceptron whose
// inputs are the linear model's own subtotals, and whose output is added back.
//
// **It is one weight as far as everything else is concerned.** One field in
// `Weights`, an array like `piece` already is, mutated element-wise by the
// tuner and validated by `withWeights` like any other. What is unusual is only
// that its *shape* is load-bearing, which is what `resizeParams` is about.
//
// **The default is all zeros, and that is a design decision rather than a
// placeholder.** A zeroed net outputs exactly nothing, so every champion
// already on disk scores precisely what it scored before this existed and no
// version number has to move. The search discovers the head; it is not imposed
// on anybody.

/**
 * Nodes per layer: twenty-five inputs through a wide waist to three outputs.
 *
 * Four layers, and the sizes are not free — with biases this is exactly
 * **271 parameters**: `25x8 + 8` + `8x5 + 5` + `5x3 + 3` = `208 + 45 + 18`.
 *
 * **The last layer widens, and that is deliberate.** The output layer fans back
 * out because the head is asked three different questions and each wants its
 * own answer. What narrows is the *representation* — seventeen numbers about a
 * board pressed into five — and the outputs are three readings of that same
 * compressed state, which is the whole reason they can disagree without needing
 * three networks.
 *
 * **There are no spare input slots left, which is why this widened.** The five
 * held open when the layer was thirteen wide are all filled — `choke`, `idle`,
 * `debt`, `inHand` and `linear` — so the four distance sensors could only
 * arrive as a reshape. A spare costs eight weights and nothing else, since an
 * input that is always zero contributes zero to every node whatever its weights
 * say; a reshape costs a `MIGRATIONS` entry, a version bump and every champion
 * re-measured. Hold slots open again the next time this is touched.
 *
 * **Filling one is still a version bump**, though — the weights on a spare
 * have been drifting under mutation all along, so the moment a real number
 * arrives they apply at once, unmeasured.
 */
export const SHAPE: readonly number[] = [25, 8, 5, 3];

/** How many numbers a network of `shape` holds, weights and biases together. */
export function paramCount(shape: readonly number[] = SHAPE): number {
  let total = 0;
  for (let i = 1; i < shape.length; i++) total += shape[i - 1] * shape[i] + shape[i];
  return total;
}

export const PARAMS = paramCount();

/**
 * What each input slot means.
 *
 * **Bucketed by what the term measures, not by `WEIGHT_GROUPS`.** That table
 * partitions the *weight list* by what each weight is about, which is the right
 * shape for a `--params` selector and the wrong one here: `evaluate` draws
 * almost every term from the `board` group and `scoreMove` almost every term
 * from `move`, so group sums would hand this network two live numbers and four
 * permanent zeros. These six are filled by both call sites, so one number means
 * one thing wherever the head is asked.
 */
export const INPUTS = [
  "material",
  "ore",
  "factory",
  "mint",
  "network",
  "move",
  /**
   * How far into the game this is.
   *
   * **The one input that is not a quantity of centipawns**, and the only thing
   * here that lets the head say *when*. Every other slot is something the
   * linear model already prices; this one prices nothing on its own and exists
   * so the network can make the others conditional — "an unbuilt factory is
   * fine on turn five and a disaster on turn eighty" is the interaction this
   * was built for. A coefficient cannot express it: a linear term on turn
   * count is a standing drift that says nothing about anything else.
   */
  "turns",
  /**
   * The linear score the head is correcting.
   *
   * **The estimate so far, fed back** — which is as close to recurrence as
   * this arrangement can honestly get. A real recurrent input would be the
   * *previous position's* estimate, and there is no previous position at one
   * of the two call sites: `evaluate` is a pure function of a board. This one
   * is available at both, needs no history threaded anywhere, and leaves the
   * evaluation a function of the position rather than of the path to it.
   *
   * What it buys is the interaction "when the rest of the model already thinks
   * this is winning, weigh the rest of it differently" — which is exactly the
   * shape a coefficient cannot hold, and the reason the head exists at all.
   *
   * No self-reference to worry about: it is one forward pass over a number
   * already computed, not a fixed point.
   */
  "linear",
  /**
   * **What this computation is for**: -1 evaluating a board, +1 scoring a move.
   *
   * The slot that makes the rest of this table possible. The standing rule is
   * that every input must be filled honestly at both call sites, because a
   * slot one of them leaves at nought means two different things to one set of
   * weights — and that rule is what kept `evaluate`-only quantities out, since
   * `viewFactory` is already computed there and would be a whole factory sweep
   * per candidate in `scoreMove`.
   *
   * Telling the head *which* it is dissolves that. A zero is no longer
   * ambiguous: it is a zero the head can condition on, and "ignore the factory
   * slots when this says scoring" is one weight away. That is precisely the
   * kind of thing a nonlinear head can hold and a coefficient cannot.
   *
   * Signed rather than 0/1, so both halves of `tanh` are used and neither mode
   * is the one that looks like an absent input.
   */
  "mode",
  /**
   * Plant on paper against plant in fact: `1 - throughput / widest`.
   *
   * `throughput` is the narrowest stage and `widest` the widest, so six diggers
   * and one oven reads wide and delivers one oven. This is the gap between the
   * two, and it is the first of three slots that exist to say *what we see is
   * not what we get*.
   *
   * **Against `widest`, and it used to be against `capacity`, which is not a
   * plant's width at all.** `capacityValue` scores the stages a side has *no*
   * machine for, so it is nought for anybody holding a pawn, a king and a rook:
   * measured over six hundred boards across eleven armies, this slot was a flat
   * zero on every board of every game but `classic`'s — the army with no
   * factory, where it was a flat one. Every promoted champion had this column
   * searched against a constant. See `PLANNER_VERSION` v23.
   *
   * `evaluate` only. Zero when `mode` says scoring.
   */
  "choke",
  /**
   * Metal held that can buy nothing: the share of `banked` beyond `readiness`.
   *
   * A hopper the evaluation prices as material and the side cannot spend. The
   * same divergence as `choke` one stage further down the line.
   *
   * `evaluate` only.
   */
  "idle",
  /**
   * Track drawn that the ore cannot reach: `drawn * (1 - affordability)`.
   *
   * Plan against delivery. `w.beltDebt` already gates one decision on this
   * ratio; as an input the head can learn how much unfunded plan is tolerable
   * rather than reading a single threshold.
   *
   * `evaluate` only.
   */
  "debt",
  /**
   * What the moving piece has in hand, priced by grade.
   *
   * **`inHand`, not `carrying`.** "Carry" already means the drag-and-drop
   * gesture. One word for two mechanics is how a slot gets filled with the
   * wrong number.
   *
   * **`scoreMove` only — the mirror of the three above.** There is no moving
   * piece in `evaluate`, so this is nought there, and `mode` is what makes
   * that an honest zero rather than "carrying nothing". The two halves of the
   * table now lean opposite ways, which is the shape the flag bought.
   *
   * Value rather than a count: `carriedValue` prices by grade, so a pawn with
   * two lumps of scrap and a rook with twenty bars of diamond are not the same
   * input. `load` already computes it for the haul term, so this costs a
   * hoisted local and nothing else.
   *
   * What it lets the head say is the thing the linear terms keep circling: a
   * move is worth something different when the piece making it is the one
   * holding the metal. `cargoAtRisk` prices hanging it, `haul` prices carrying
   * it somewhere — neither can make *every other term* conditional on it.
   */
  "inHand",
  /**
   * How close the army stands to ore, on average.
   *
   * Mean over every piece of ours of the distance to the nearest workable
   * heap. **A mean and not a minimum**: one pawn sitting on scrap says the
   * army found some, and what the head is being given here is whether the
   * *army* is where the ore is — which is the thing `mining` and `nearestHeap`
   * price one piece at a time and no coefficient can say about the whole.
   *
   * `evaluate` only, and nought when there is no ore to be near.
   */
  "scrapNear",
  /**
   * How close the army's centre of mass is to the nearest enemy piece.
   *
   * **A long gradient, which is what the piece-by-piece terms cannot be.**
   * `aggression` pays per move for ground closed by the *mover*; this says
   * where the army as a body is relative to the fight, so the head can make
   * everything else conditional on it — an unbuilt factory means one thing
   * with the enemy eight squares out and another with them thirty away.
   *
   * Centroid rather than nearest-of-ours on purpose: the nearest is one
   * skirmisher and would swing wildly as it trades.
   */
  "foeNear",
  /**
   * How close the army's centre of mass is to the nearest objective.
   *
   * **The pointable one.** `nearestObjective` is already the one sensor a board
   * can be *aimed* with — everything else the planner measures is derived from
   * the position, and none of it can say "go there" about ground that is not
   * otherwise interesting. This is that, asked about the army as a body, so a
   * level or the royale's ring can pull a whole force without a weight per
   * piece.
   *
   * Nought where nothing has been pointed at, which is most boards.
   */
  "focusNear",
  /**
   * How tightly the army is gathered around that same point.
   *
   * The mean of each piece's own distance to the nearest objective, against
   * `focusNear`'s single centroid reading. Two numbers about one settable
   * point and they answer different questions: *where* the force is, and
   * whether it is a force or a smear. An army straddling two objectives has a
   * centroid between them and a wide spread; one massed on a single objective
   * has both small.
   *
   * The second of the two slots held for in-game aiming.
   */
  "focusSpread",
  /**
   * Whether the army has been told to be forging: +1 yes, -1 no, 0 nobody said.
   *
   * **A mask, and the only input here that is an *order* rather than an
   * observation.** Everything above is something about the board — how much
   * metal, how far the enemy, how choked the plant — and the head's job with
   * them is to weigh. This one is the board telling it what to do, and what it
   * is for is the shape no coefficient holds: `forgeAppetite` can say "this
   * army likes minting" and cannot say "and the opposite when this flips".
   *
   * **It exists because a harvesting party runs out of diggers.** Only pawns
   * work heaps, a crew is bounded by how many of it can dig, and the only way
   * to get another is to make one — so forging is that profile's signature act
   * rather than the freelancing its goal used to charge for outright. A minted
   * pawn faces away from whatever made it, so a knight's re-aim is the next
   * thing the board asks for; whether the search finds that is the question the
   * instrument is being pointed at.
   *
   * **Its own slot, where the harvest light needed none.** That one is carried
   * by `focusNear` going quiet — a target that is there or is not — and this
   * has nowhere to point: it is a yes or a no about the whole army. Filled at
   * both call sites from `Position.signals`, which is nought on every ordinary
   * board, so the slot reads "no orders" everywhere the trainer is not.
   */
  "forgeWanted",
  /**
   * **What this decision changed**, subtotal by subtotal — the six slots that
   * make the head a chooser rather than a describer.
   *
   * Measured before they existed: over forty candidate moves one ply apart the
   * head's whole contribution ranged **0 to 5 centipawns** against a linear
   * span of 17 to 66, for ten of eleven promoted champions. It discriminated
   * *situations* — 66 to 706 centipawns across very different boards — and not
   * candidates within a turn, which is the only comparison the search ever
   * makes. A contribution that is near-constant across a candidate set cancels
   * exactly, which is `headResponse`'s own argument about a flat head applied
   * to the distribution the game actually produces.
   *
   * The cause is the squash: `tanh(x / INPUT_SCALE)` over a subtotal that
   * moves by tens against a range of thousands is a flat line. So these carry
   * the *difference* and are scaled by what a decision moves rather than by
   * what a board holds (`DELTA_SCALE`).
   *
   * **One meaning at both call sites**, which is the standing rule for a slot.
   * `scoreMove`'s subtotals are already differences — that is what scoring a
   * move *is* — so they fill these directly; `evaluate` fills them against the
   * board the plan started from, and against nothing when a caller has no
   * baseline to offer, which is the zero every stored champion arrives with.
   */
  "dMaterial",
  "dOre",
  "dFactory",
  "dMint",
  "dNetwork",
  "dLinear",
  /**
   * Which game the army was told to play: +1 chess, -1 factory, 0 nobody said
   * (§32 L1, `modeSignal`).
   *
   * **`playMode`, never `mode`** — that slot is which computation this is.
   * An order like `forgeWanted`, read off `Position.signals` at both call
   * sites. **Last, because a grow only preserves behaviour at the end**:
   * `resizeParams` keeps old input `i` at slot `i` and pads what is past the
   * old width, so an input inserted mid-table would shift every weight after
   * it onto a stranger's input.
   */
  "playMode"
] as const;

export type Inputs = Record<(typeof INPUTS)[number], number>;

export const noInputs = (): Inputs => ({
  material: 0,
  ore: 0,
  factory: 0,
  mint: 0,
  network: 0,
  move: 0,
  turns: 0,
  linear: 0,
  mode: 0,
  choke: 0,
  idle: 0,
  debt: 0,
  inHand: 0,
  scrapNear: NO_TARGET,
  foeNear: NO_TARGET,
  focusNear: NO_TARGET,
  focusSpread: NO_TARGET,
  // Nought is "no orders" and is the ordinary board, so this one is a plain
  // zero rather than a sentinel. See `forgeSignal`.
  forgeWanted: 0,
  // **Nought is "no baseline", and it is also no change.** The two readings
  // agree, which is what makes the absent case safe: a caller with nothing to
  // compare against contributes nothing rather than something wrong.
  dMaterial: 0,
  dOre: 0,
  dFactory: 0,
  dMint: 0,
  dNetwork: 0,
  dLinear: 0,
  // "No orders", the ordinary board — a plain zero, like `forgeWanted`.
  playMode: 0
});

/**
 * Centipawns per unit of input, before the squash.
 *
 * The subtotals arrive in centipawns and run to thousands; `tanh` saturates
 * past about three, so feeding them raw would make every input either -1 or +1
 * and the network a lookup table on signs. About a queen, so a queen's worth of
 * anything is one unit of signal.
 */
const INPUT_SCALE = 900;

/**
 * Centipawns per unit of *difference*.
 *
 * **Sized to what a decision moves, not to what a board holds**, which is the
 * whole of why the delta slots exist. `INPUT_SCALE` is about a queen because a
 * subtotal is; a difference between two candidates is a piece at most and
 * usually tens, so on that scale it would arrive as the same flat nought the
 * absolutes do. Two hundred puts a pawn's worth of change near a fifth of
 * `tanh`, a rook near the middle and a queen at the top of it.
 */
const DELTA_SCALE = 200;

/**
 * Centipawns per unit of the *whole score*, which is a different quantity.
 *
 * **`linear` is a sum of the six subtotals, so it spans what they span added
 * up, and on `INPUT_SCALE` the top half of the range is gone.** Measured over a
 * played game (the numbers `headRange` is sized from): |linear| runs to a
 * median of 628, a p90 of 2126 and a max of 2982. At `tanh(v / 900)` that is
 * 0.60, 0.982 and 0.997 — a p90 board and the best board ever seen differ by
 * **0.015 of the input's range**, and everything past 1500 is inside the last
 * seven percent of it. The head can rank a level game and cannot rank a won
 * one, which is the ordering a rollout needs most.
 *
 * Nothing in the search can repair that: the input is already flattened before
 * a weight touches it, which is why the field's `linear` sensitivity looks
 * healthy (63cp mean) while being a reading of the bottom of the scale only.
 *
 * At 2500 the same four boards are 0.25, 0.69 and 0.83 — monotone and
 * distinguishable across the range the evaluation actually occupies, with the
 * rail left for boards past anything measured. **This is the input that has to
 * carry a full-blend evaluation**: at `headBlend` 1 the head *is* the score, so
 * a saturated `linear` is a scoreboard that stops counting.
 */
const LINEAR_SCALE = 2500;

/**
 * Turns, centred and scaled so the clock uses the whole of `tanh`.
 *
 * **Its own transform, because it is not a quantity of centipawns.** Divided
 * by `INPUT_SCALE` a game of eighty turns spans 0..0.09, which is the flat
 * middle of `tanh` — the input would be present, cost three parameters, and
 * carry almost no signal. That is the same failure a flat 0.05 seed turns out
 * to have, arrived at from the other end.
 *
 * Centred on `TURN_MID` so the sign carries the meaning: negative early,
 * positive late, saturating somewhere past a long game. A head that wants
 * "only in the endgame" gets it from one weight.
 */
/**
 * "There is no such thing here", for the four distance slots.
 *
 * **A sentinel rather than a zero, because zero is the strongest reading these
 * slots have.** They are proximities: nought distance means standing on the
 * thing. A board with no ore on it and a board with a pawn sitting on a heap
 * are the two ends of that scale, and filling the absent case with 0 would put
 * them in the same place — the same ambiguity `mode` was added to dissolve for
 * the factory slots. The transform maps this back to a flat nought *after* the
 * squash, so absent reads as no opinion rather than as either extreme.
 *
 * **A floor, not an equality**, which is why `proximity` tests `>=`. Past any
 * board's diagonal by a wide margin, and *below* `opponent.ts`'s own `FAR`
 * (999) — what its sweeps answer when they find nothing — so that value lands
 * in the absent bucket without either file naming the other's constant. Read as
 * an equality it would not: 999 is not 900, and the slot would come back as the
 * strongest "far away" reading the scale has instead of as no opinion.
 */
export const NO_TARGET = 900;

/**
 * Where a distance stops being interesting, and how fast it gets there.
 *
 * Centred so the sign carries the meaning, like `turns`: **positive is near**.
 * These slots are named `...Near` and a head reading "bigger is further" from a
 * name that says the opposite is the kind of thing nobody catches until the
 * search has spent a day on it. At `PROX_MID` the input is nought, on top of
 * the thing it is about +0.76, and a board away -0.96.
 */
const PROX_MID = 8;
const PROX_SCALE = 8;

const TURN_MID = 40;
const TURN_SCALE = 40;

/**
 * What `mode` says — which question the head is being asked.
 *
 * Signed, so no call site looks like an absent input. **Zero is not available**
 * even though it is the obvious third value: `noInputs` fills every slot with
 * it, so a mode of nought is exactly what a caller that forgot to say reads
 * as, and a flag whose "unset" and "belt" are the same number is the
 * `KING_PLAIN is 0` trap with a different name.
 *
 * The head is asked four different questions on the same thirteen inputs, and
 * this is the whole of how it can tell them apart — a network that could not
 * would have to answer all four with one function of the board.
 */
export const MODE_EVALUATE = -1;
export const MODE_SCORE = 1;
/** How much track to lay. Asked once a turn, in the planner. */
export const MODE_BELT = 2;
/**
 * How hard to think this turn. Asked once a turn, before any candidate exists.
 *
 * **Its own mode rather than a reading of the score pass**, and the reason is
 * that the two questions are asked at different moments about different
 * things. `MODE_EVALUATE` is asked of every board a rollout reaches, hundreds
 * of times a turn, and `MODE_SCORE` of every candidate move; this is asked
 * once, of the root, before a single candidate has been generated — because
 * the answer *decides how many* rollout turns those passes will run for. A
 * flag the network can see is what lets the same thirteen inputs mean "is this
 * position good" in one pass and "is this position worth thinking about" in
 * the next, and those are genuinely different: a losing position is bad and
 * worth thinking about, and a quiet industrial turn is fine and is not.
 */
export const MODE_DEPTH = 3;

/**
 * How each slot reaches `tanh`, for the slots that are not centipawns.
 *
 * **Every one of these is a scale that had to be chosen rather than
 * inherited**, and getting one wrong is silent: an input squashed into the
 * flat middle of `tanh` is present, costs three weights and carries no signal.
 * That is what a flat 0.05 seed turned out to be, and what dividing a turn
 * count by nine hundred would have been.
 *
 * - `turns` is centred on a middlegame, so the sign says early or late.
 * - `linear` is the six subtotals added up, so it spans further than any one of
 *   them and needs its own scale. See `LINEAR_SCALE`.
 * - `mode` and `forgeWanted` are already ±1 and are passed through untouched;
 *   squashing a flag would only blunt it.
 * - `choke` and `idle` are shares in 0..1, centred on a half so an even split
 *   reads as nothing and both directions are reachable.
 * - `debt` is a count of unfunded parts; five is a run worth noticing.
 * - `inHand` is grade-weighted value; two hundred is a working haul.
 */
const TRANSFORM: Record<string, (value: number) => number> = {
  turns: (v) => tanh((v - TURN_MID) / TURN_SCALE),
  linear: (v) => tanh(v / LINEAR_SCALE),
  mode: (v) => v,
  // **A flag, so it is passed through exactly as `mode` is.** It had no entry
  // and fell to the centipawn default, which is `tanh(±1 / 900)` — ±0.0011,
  // 686 times smaller than `choke` at full deflection and 900 times smaller
  // than the flag beside it. Measured across the whole promoted field: a sweep
  // of this slot moved the score by **exactly nought centipawns on all eleven
  // champions**. That is the failure this table exists to prevent, and the
  // `mode` test one file over ("is passed through rather than squashed")
  // describes it in so many words while testing the other flag.
  //
  // It was read as nothing having learned to condition on the signal yet. No
  // head could have: for this column to reach the output its layer-one weights
  // would have to be some seven hundred times the rest, which saturates the
  // node on that one input.
  forgeWanted: (v) => v,
  // A flag too, passed through for `forgeWanted`'s reason.
  playMode: (v) => v,
  choke: (v) => tanh((v - 0.5) * 2),
  idle: (v) => tanh((v - 0.5) * 2),
  debt: (v) => tanh(v / 5),
  // Grade-weighted, so a rook full of diamond runs to several hundred while a
  // pawn with scrap is in the tens. Two hundred puts a normal haul near the
  // middle of `tanh` and leaves a hoard reaching the top of it.
  inHand: (v) => tanh(v / 200),
  // The four distances, all on the same scale so one number means one thing
  // across them, and all answering a flat nought when there is nothing of that
  // kind on the board. See `NO_TARGET`.
  scrapNear: proximity,
  foeNear: proximity,
  focusNear: proximity,
  focusSpread: proximity,
  dMaterial: (v) => tanh(v / DELTA_SCALE),
  dOre: (v) => tanh(v / DELTA_SCALE),
  dFactory: (v) => tanh(v / DELTA_SCALE),
  dMint: (v) => tanh(v / DELTA_SCALE),
  dNetwork: (v) => tanh(v / DELTA_SCALE),
  dLinear: (v) => tanh(v / DELTA_SCALE)
};

/** A distance, as "how near": positive close, negative far, nought for absent. */
function proximity(value: number): number {
  if (!Number.isFinite(value) || value >= NO_TARGET) return 0;
  return tanh((PROX_MID - value) / PROX_SCALE);
}

/**
 * One input, squashed by whichever transform its slot uses.
 *
 * **Exported for the tests, because a claim about a transform has to be tested
 * on the transform.** Asking it through `forward` measures the squash *and*
 * whether a hashed 207-number network happens to be monotone in that one slot,
 * and the second half is luck that runs out at the next reshape: the centring
 * test passed at 71 parameters and at 175 and failed at 207, with the transform
 * untouched in all three.
 */
export const squash = (name: (typeof INPUTS)[number], value: number): number =>
  (TRANSFORM[name] ?? ((v: number) => tanh(v / INPUT_SCALE)))(value);

/**
 * The most the head may move a score, in centipawns.
 *
 * The output is a `tanh`, so this is a hard cap rather than a scale factor —
 * which is the point. Every other weight here is sized against the smallest
 * decision it must not swamp; this one cannot be sized that way, because what
 * it *does* is whatever the search makes it do. So it is bounded instead: at
 * 200 a saturated head is worth about two pawns, enough to change which of two
 * close boards is preferred and not enough to outvote a piece.
 */
/**
 * How many centipawns the head may move a score by, when nobody says otherwise.
 *
 * **The default of a weight now, not a constant.** It was the one number
 * deciding how much the nonlinear half of the evaluation was allowed to matter
 * against the linear half, and it was the same for every army — so a style
 * whose decisions are mostly interactions and one whose claim is a straight
 * material count leaned on the network exactly as hard. `w.head` is what makes
 * that a thing an army declares and the search moves.
 */
export const OUTPUT_SCALE = 200;

const tanh = (x: number): number => Math.tanh(x);

/**
 * Run the network. Returns centipawns to add to the score.
 *
 * Guarded on shape rather than trusting the caller: these params come off disk
 * from a champion file, and a vector of the wrong length would otherwise read
 * garbage off the end of the array as `undefined` and return `NaN` — which
 * loses every comparison, so the opponent would quietly stop playing rather
 * than fail. The same argument `withWeights` makes about non-finite weights.
 */
export function forward(
  params: readonly number[],
  inputs: Inputs,
  scale = OUTPUT_SCALE
): number {
  return run(params, inputs, scale).score;
}

/**
 * How far the belt appetite may swing, as an exponent.
 *
 * The second output is a **multiplier on how much unfunded track the planner
 * will tolerate**, so it is exponentiated rather than added: a multiplier must
 * never reach zero or go negative, and the two directions have to be
 * symmetric — half as much track and twice as much are the same size of
 * decision. At 1, a saturated output is e times or a third of the default.
 */
const BELT_SCALE = 1;

/**
 * Which output node answers the score question.
 *
 * **The one output that is not squashed, and the reason is resolution at the
 * top of the range.** The other two are bounded quantities by nature — a belt
 * multiplier and a share of a depth ration — so `tanh` is the right shape for
 * them. A score is not: it has to keep two *won* boards apart as reliably as it
 * keeps two level ones apart, and a squash spends almost all of its slope near
 * nought.
 *
 * Measured on the shipped seed at `headRange` 3000, which is what a full-blend
 * evaluation has to cover (|linear| p90 2126, max 2982): representing 628cp
 * needs `z` 0.21 where the node moves 28.7cp per hundredth of `z`, and
 * representing 2982cp needs `z` 2.90 where it moves **0.36cp** — an eightyfold
 * collapse in how finely the search can resolve exactly the boards it is
 * trying to rank. It was never that a won board could not be represented; it
 * was that two of them could barely be told apart.
 *
 * Linear, the node moves `scale` centipawns per unit of `z` everywhere, and the
 * bound comes back as a clamp — so `w.head` and `w.headRange` mean precisely
 * what they meant before, "the most the head may move a score".
 *
 * Knock-on worth knowing: `centreScore` becomes *exactly* order-preserving on
 * this node rather than approximately so, because subtracting a constant from
 * `z` now subtracts a constant from the answer.
 */
const SCORE_OUTPUT = 0;

/** Which output node answers the belt question. */
const BELT_OUTPUT = 1;

/** Which output node answers the depth question. */
const DEPTH_OUTPUT = 2;

/** Every answer, from one pass. */
export interface Head {
  /** Centipawns to add to the score. */
  score: number;
  /**
   * What to multiply the belt-debt allowance by. **1 when the output is zero**,
   * which is what every migrated champion arrives with — so a resized network
   * lays exactly the track it laid before until the search moves it.
   */
  belt: number;
  /**
   * How far from the ordinary search depth to go, as a signed share in −1..1.
   *
   * **Deliberately not plies.** How many turns of rollout a share buys depends
   * on `w.depthSpread`, and how many are *affordable* depends on `w.rolloutTurns`
   * and what is in the bank — all three of which are weights this file cannot
   * see. So the head answers the part that is genuinely its own ("harder than
   * usual, or easier?") and `searchDepth` in `opponent.ts` does the arithmetic.
   *
   * **Zero is the ordinary depth**, which is what a migrated champion arrives
   * with, so a resized network thinks exactly as long as it always did.
   */
  depth: number;
}

/**
 * Run the network.
 *
 * Guarded on shape rather than trusting the caller: these params come off disk
 * from a champion file, and a vector of the wrong length would otherwise read
 * garbage off the end of the array as `undefined` and return `NaN` — which
 * loses every comparison, so the opponent would quietly stop playing rather
 * than fail. The same argument `withWeights` makes about non-finite weights.
 */
export function run(
  params: readonly number[],
  inputs: Inputs,
  /**
   * What the score answer is worth, in centipawns at full deflection.
   *
   * Passed rather than read from a constant so it can be an army's own
   * (`w.head`). The other answers deliberately do *not* take it: `belt` is a
   * bounded multiplier and `depth` is a share, and how loudly a head may speak
   * about a *score* says nothing about either.
   */
  scale = OUTPUT_SCALE
): Head {
  if (params.length !== PARAMS) return { score: 0, belt: 1, depth: 0 };

  let layer: number[] = INPUTS.map((name) => squash(name, inputs[name]));
  let at = 0;
  for (let l = 1; l < SHAPE.length; l++) {
    const last = l === SHAPE.length - 1;
    const next: number[] = [];
    for (let j = 0; j < SHAPE[l]; j++) {
      let sum = 0;
      for (let i = 0; i < SHAPE[l - 1]; i++) sum += layer[i] * params[at++];
      sum += params[at++];
      // **Every node is squashed but the score**, which keeps its slope right
      // across the range instead of spending it all near nought. The hidden
      // layers still need theirs — a network of linear layers is one linear
      // layer, and the whole reason this exists is to hold interactions a
      // coefficient cannot. See `SCORE_OUTPUT`.
      next.push(last && j === SCORE_OUTPUT ? sum : tanh(sum));
    }
    layer = next;
  }
  return {
    // Bounded by a clamp rather than by the activation, so `scale` still means
    // "the most the head may move a score" and a saturated head is still worth
    // about two pawns at the default. What changed is only how the range is
    // *reached*: linearly, so the last centipawn is as reachable as the first.
    score: clampTo(layer[SCORE_OUTPUT] * scale, scale),
    belt: Math.exp(layer[BELT_OUTPUT] * BELT_SCALE),
    depth: layer[DEPTH_OUTPUT]
  };
}

/** `value`, held inside ±`limit`. */
const clampTo = (value: number, limit: number): number =>
  value > limit ? limit : value < -limit ? -limit : value;

/**
 * What a parameter is worth when there is no data for it.
 *
 * **Not zero, and that is the whole point.** A zeroed head is *exactly* the
 * degenerate case: every node sums to nothing, `tanh(0)` is 0 at every layer,
 * the output is a flat 0 whatever the board looks like, and a fitness handed a
 * constant has nothing to select on. Multiplicative mutation cannot leave zero
 * either — `jitter` kicks one additively, but only in the generations where it
 * happens to pick that key, so a dead head can sit through a whole search.
 * Measured across the promoted field: **four of eight heads were inert**, one
 * of them with all thirty-two parameters still exactly zero.
 *
 * **0.05 everywhere was the first answer and it does not propagate.** Measured
 * over four hundred boards: output std **0.01** — a flat 5.25 — and
 * `isFlatHead` rightly calls it dead, which would have `reviveHead` replacing
 * a fresh seed with another fresh seed forever. The arithmetic is why: every
 * layer multiplies the signal it passes on by roughly the size of its own
 * weights, so through `6 -> 3 -> 2 -> 1` about 0.05^3 of the input survives
 * and the answer is the last bias times `OUTPUT_SCALE`.
 *
 * The measured threshold is near **0.5** — where the output's std passes ten
 * and a head begins answering different boards differently — which is very
 * nearly `1/sqrt(fan_in)` at these widths (0.41, 0.58, 0.71). So this is a
 * *gain* applied per layer rather than a flat value, which is the ordinary way
 * to initialise a network and here is also where the measurement lands.
 */
export const SEED_GAIN = 1;

/**
 * How big each parameter is supposed to be, slot by slot.
 *
 * **A network's weights are not on the weight table's scale, and the search did
 * not know it.** `jitter` kicks a weight sitting at exactly zero to
 * `±scale × 10` — about ±3.5 at the default — which is the right size of jump
 * for a coefficient in a table running to thousands and is twelve times too big
 * for a parameter whose live value is `1/√13`. One weight of 3.5 saturates the
 * node it feeds. Measured over two hundred trials: **a single `mutate` pass
 * over a fresh seed head took it from a response of 16 centipawns to a median
 * of nought**, with 56% of sampled boards pinned at the output rail — so the
 * first time the search touched a head it killed it, and `isFlatHead` then
 * called the corpse healthy.
 *
 * That is why this is published rather than kept inside `seedParams`: `mutate`
 * and `revive` are generic over a weight vector and cannot know that one field
 * is a network. Both read this.
 *
 * Fan-in scaled, which is where `seedParams` already lives and where this file
 * has already measured a head coming alive. **Biases take their layer's scale
 * too** — a bias is the one part of a node that does not depend on the board,
 * so an oversized one is the fastest way to pin it.
 */
export const HEAD_SCALE: readonly number[] = (() => {
  const out: number[] = [];
  for (let l = 1; l < SHAPE.length; l++) {
    const scale = SEED_GAIN / Math.sqrt(SHAPE[l - 1]);
    for (let j = 0; j < SHAPE[l]; j++) {
      for (let i = 0; i < SHAPE[l - 1]; i++) out.push(scale);
      out.push(scale);
    }
  }
  return out;
})();

/**
 * A board's worth of inputs, filled the way the two call sites fill them.
 *
 * **The sampling is the whole of whether a flatness test means anything, and
 * the first one did not.** It rolled every slot uniformly across ±1200,
 * including `mode` — which is passed through the squash untouched and in play
 * is only ever −1, 1, 2 or 3. At ±1200 that one input dwarfs the other twelve,
 * every node saturates on its sign, and the sampled output is a two-valued
 * function of a flag. That reads as spread. Measured against the promoted
 * field: ravager scored 33.2 on the old sampling and **1.8** with `mode` held
 * to a value the game passes, classic 58.6 and **3.5** — two heads doing
 * nothing, both certified live.
 *
 * So `mode` is one of the four, and the rest are drawn across the range their
 * own transform is written for. Which slots are filled follows the call sites:
 * `evaluate` fills the six subtotals, the clock, the linear score and the three
 * factory slots and leaves `inHand` at nought; `scoreMove` is the mirror. A
 * head probed on inputs that never co-occur is a head measured on boards it
 * will never be shown.
 */
function probeBoard(next: () => number, mode: number): Inputs {
  const inputs = noInputs();
  inputs.material = (next() - 0.5) * 3000;
  inputs.ore = (next() - 0.5) * 800;
  inputs.factory = (next() - 0.5) * 1200;
  inputs.mint = (next() - 0.5) * 800;
  inputs.network = (next() - 0.5) * 600;
  inputs.move = (next() - 0.5) * 600;
  inputs.turns = next() * 120;
  // The sum, not a seventh independent draw: this slot *is* the other six added
  // up, and a probe where it disagrees with them is a board that cannot happen.
  inputs.linear =
    inputs.material + inputs.ore + inputs.factory + inputs.mint + inputs.network + inputs.move;
  inputs.mode = mode;
  if (mode === MODE_SCORE) {
    // Most moves are made by a piece holding nothing.
    inputs.inHand = next() < 0.6 ? 0 : next() * 500;
  } else {
    // `beltAppetite` and `searchDepth` both fill their inputs through
    // `evaluate` and then swap the flag, so all three modes land here.
    inputs.choke = next();
    inputs.idle = next();
    inputs.debt = next() * 12;
  }
  return inputs;
}

/** What a head does across a sample of boards. Each field is "how much". */
export interface HeadResponse {
  /**
   * Spread of the score answer, in centipawns at the default scale.
   *
   * **The spread and never the mean, because a constant cancels.** An
   * evaluation is only ever compared with another evaluation, so a head
   * answering +58 on every board moves no decision at all.
   */
  score: number;
  /**
   * How far the belt multiplier ever gets from a flat 1.
   *
   * **Distance from neutral, not spread, and the difference is the whole
   * reason this is a separate number.** A constant belt answer does *not*
   * cancel — it multiplies `w.beltDebt`, so a head pinned at 2.68 has doubled
   * how much unfunded track its army will draw. That is a finding, and
   * re-flashing the vector would throw it away.
   */
  belt: number;
  /** How far the depth share ever gets from a flat 0. Neutral is nought. */
  depth: number;
  /**
   * How far the depth answer moves *between* boards, high water to low.
   *
   * **A range, where `depth` is a distance from neutral, and the third output
   * is the one place those two come apart.** What the depth answer buys is an
   * *allocation*: `searchDepth` grants the ration plus whatever was banked, the
   * bank floors at nought, and a turn that spends the ration banks nothing — so
   * a head that asks for the same thing on every board gets nothing whichever
   * value it asks for. A constant *above* the ration is simply never affordable
   * (measured in `tuning.ts`: a head pinned to "deepest available" played four
   * games at a mean depth of 5.00, identical to a silent head), and a constant
   * *below* it is a standing cut to the ration with no deep turn ever paid for
   * out of it. Both are the mechanism switched off, and `depth`'s distance from
   * neutral calls the second one busy.
   *
   * Measured across the promoted field: `classic` 0.20 flat, `ravager` 0.81
   * ±0.02, `trainer` 0.73 ±0.04, `replicator` −0.80 ±0.12 — four heads holding
   * the third output still, one of them a permanent self-handicap.
   */
  depthSwing: number;
}

/**
 * How near neutral a non-score answer has to be to count as doing nothing.
 *
 * Conservative on purpose: the thing gated on these is destructive. `belt`
 * multiplies an allowance, so a percent or two is inside the noise of the
 * decision it feeds; `depth` is multiplied by `w.depthSpread` (about two) and
 * then **rounded to whole rollout turns**, so a share under a quarter cannot
 * move the answer and a twentieth is nowhere near.
 */
const BELT_EPS = 0.02;
const DEPTH_EPS = 0.05;

/**
 * Run a head over a fixed sample of boards and report what it did.
 *
 * **The seed is fixed**, so this is a pure function of the parameters —
 * `src/engine/ai/` may not read a clock or roll a die, and a head judged differently
 * on two clients is a desync. Sampled rather than reasoned about: thirteen
 * inputs into a bounded nonlinearity has no closed form for "does this
 * saturate", and the network is small enough that a few dozen forward passes
 * are free. It runs on the read path (`withWeights`), which is once a planned
 * turn rather than once a node.
 */
export function headResponse(params: readonly number[], samples = 64): HeadResponse {
  if (params.length !== PARAMS) return { score: 0, belt: 0, depth: 0, depthSwing: 0 };
  let seed = 20260810;
  const next = (): number =>
    (seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0) / 4294967296;
  const modes = [MODE_EVALUATE, MODE_SCORE, MODE_BELT, MODE_DEPTH];
  // **Per mode, and pooling them is the same mistake one level up.** A head
  // that answers −174 to every board it evaluates and +150 to every move it
  // scores is constant at both call sites and moves nothing at either — but
  // pooled, those two constants are a spread of a hundred and sixty. Caught by
  // the test for it: a deliberately saturated head reported 198 centipawns of
  // "response", every bit of it the flag.
  const scores = new Map<number, number[]>([
    [MODE_EVALUATE, []],
    [MODE_SCORE, []]
  ]);
  let belt = 0;
  let depth = 0;
  let depthLow = Infinity;
  let depthHigh = -Infinity;
  for (let i = 0; i < samples; i++) {
    const mode = modes[i % modes.length];
    const out = run(params, probeBoard(next, mode));
    scores.get(mode)?.push(out.score);
    if (mode === MODE_BELT) belt = Math.max(belt, Math.abs(out.belt - 1));
    if (mode === MODE_DEPTH) {
      depth = Math.max(depth, Math.abs(out.depth));
      depthLow = Math.min(depthLow, out.depth);
      depthHigh = Math.max(depthHigh, out.depth);
    }
  }
  // The louder of the two questions: a head earning its place at one call site
  // is doing something, even if it has nothing to say at the other.
  let score = 0;
  for (const seen of scores.values()) {
    if (seen.length === 0) continue;
    const mean = seen.reduce((a, x) => a + x, 0) / seen.length;
    const variance = seen.reduce((a, x) => a + (x - mean) ** 2, 0) / seen.length;
    score = Math.max(score, Math.sqrt(variance));
  }
  return {
    score,
    belt,
    depth,
    depthSwing: depthHigh > depthLow ? depthHigh - depthLow : 0
  };
}

/**
 * Is this head the same whatever the board is?
 *
 * **A bad state, and the one a parameter test cannot see.** A network of
 * perfectly ordinary non-zero numbers can still answer the same value for
 * every position — the layers saturate, or the signs cancel — and then its
 * gradient is zero everywhere without a single zero in it. Measured across a
 * promoted field: one head was a flat -20 over four thousand sampled
 * boards and another a +58 with a wiggle of sixteen. **A constant
 * cancels**, because an evaluation is only ever compared with another
 * evaluation, so both were contributing exactly nothing while looking trained.
 *
 * **All three answers, and that is not the obvious reading.** The tempting
 * definition is "the score never moves", and it condemns heads that are doing
 * real work: a constant score cancels, but a constant *belt* answer of 2.68
 * doubles how much unfunded track an army draws and a constant *depth* answer
 * changes how long it thinks. Measured across the promoted field, four heads
 * are silent on score and off neutral on belt — re-flashing those throws away
 * the one thing the search found. So this is the conservative test, because
 * what it gates is destructive. `isDullHead` is the softer one, and what that
 * gates is not.
 *
 * The score threshold is a tenth of a pawn of swing: below that it cannot tip a
 * decision it is sharing a score with terms priced in hundreds.
 */
export function isFlatHead(params: readonly number[], threshold = 10): boolean {
  if (params.length !== PARAMS) return true;
  const response = headResponse(params);
  return (
    response.score < threshold && response.belt < BELT_EPS && response.depth < DEPTH_EPS
  );
}

/**
 * Is the *interaction* this head exists for absent?
 *
 * The head is here to say things a coefficient cannot — "material matters less
 * once the factory is running" — and all of those are answers to the score
 * question. A head whose score answer is a constant has failed at that,
 * whatever else it may be doing with the other two outputs.
 *
 * **So this is a softer test than `isFlatHead`, and it is safe to be softer
 * because what it triggers is additive**: the tuner offers a renormalised twin
 * *beside* the incumbent, which then has to win on the same fitness as
 * everything else. Nothing is thrown away on a false positive — a wasted
 * candidate is the whole cost. `isFlatHead` is the destructive one and stays
 * strict.
 */
export function isDullHead(params: readonly number[], threshold = 10): boolean {
  if (params.length !== PARAMS) return true;
  return headResponse(params).score < threshold;
}

/**
 * The least the depth answer may move between boards and still be doing a job.
 *
 * Multiplied by `w.depthSpread` (up to four) and **rounded to whole rollout
 * turns**, so a swing under a quarter cannot change the answer at the ordinary
 * spread of about two, and is one turn at the widest spread a search may ask
 * for. Loose on purpose: what this gates is additive.
 */
const DEPTH_SWING = 0.25;

/**
 * Is the third output stuck, whatever value it is stuck at?
 *
 * **The depth answer is an allocation, so a constant is the mechanism switched
 * off in both directions.** `searchDepth` grants the ration plus what was
 * banked, the bank floors at nought, and a turn that spends the ration banks
 * nothing — so the only road to a deep turn is a shallow one first. A head
 * asking for the same thing on every board never takes that road: a constant
 * above the ration is simply never affordable, and a constant below it is a
 * standing cut that buys no deep turn anywhere. `tuning.ts` measured the first
 * half directly — a head pinned to "deepest available" played four games at a
 * mean depth of 5.00, exactly what a silent head plays.
 *
 * **A range and not a distance from neutral**, which is what makes this a
 * different question from `isFlatHead`'s `depth` term. That one asks whether
 * the answer is *off* neutral, and correctly protects a constant belt
 * multiplier — a constant there does real work. A constant here does none, so a
 * head can be busy by that test and idle by this one, and four of the promoted
 * field are: `classic` 0.20 flat, `ravager` 0.81 ±0.02, `trainer` 0.73 ±0.04,
 * `replicator` −0.80 ±0.12, the last of them a permanent self-handicap.
 *
 * **Additive, like `isDullHead` and unlike `isFlatHead`.** What it gates is the
 * tuner offering a renormalised twin *beside* the incumbent, which then has to
 * win on the same fitness as everything else — so a false positive costs one
 * candidate and nothing is thrown away. It is deliberately not a fitness term:
 * scoring the *mechanism* rather than the outcome is the line
 * `DIAGNOSTIC_METRICS` already draws for `depth` itself, and a goal that paid
 * for a wobbly depth answer would be paying for thinking.
 */
export function isIdleDepth(params: readonly number[]): boolean {
  if (params.length !== PARAMS) return true;
  return headResponse(params).depthSwing < DEPTH_SWING;
}

/**
 * The most a bias may shift a node, once its weights are on scale.
 *
 * A node's pre-activation is `w·x + b`, and after the rescale below the board's
 * half of that is about one — so this is half a standard deviation: plainly an
 * opinion, and not enough to decide the node without the board.
 *
 * **Measured against the five dull heads in the promoted field**, whose
 * response in centipawns comes back as a clean function of how much room a bias
 * is left: at no clamp 0.3 / 0.0 / 8.6 / 0.5 / 22.3, at two 0.4 / 1.1 / 8.6 /
 * 0.7, at one 2.4 / 6.2 / 13.1 / 3.7, and at a half **10.5 / 13.1 / 31.8 / 17.0
 * / 22.3 — every one of them awake**. A quarter wakes them further and is
 * fitting the number to five vectors; a half is where they cross the bar this
 * file already uses for "can tip a decision".
 */
const BIAS_ROOM = 0.5;

/**
 * A head that has stopped reading the board, rescaled until it reads it again.
 *
 * **The rescue that keeps the finding, where a re-seed throws it away.** What
 * the search paid for is a *direction* — which way in board space each node
 * leans — and that survives both operations here exactly, because scaling by a
 * positive number and shrinking a constant are monotone and neither can reorder
 * a layer's own answers.
 *
 * **Two faults, and the second is the one the field actually has.** Measured
 * across the promoted champions: the dead heads are not weight-saturated at
 * all — their weight RMS sits *below* the seed's gain (0.17 against 0.28) while
 * their biases run to two and three against a natural pre-activation of about
 * one. They are **bias-dominated**: a constant that drowns the board rather
 * than weights that pin the node. A weights-only rescale not only misses that,
 * it makes it worse — scaling the bias up with the weights took ravager from
 * 0.6 centipawns of response to 0.3.
 *
 * So: the weights are scaled to `seedParams`' own gain, which is the size this
 * file has measured a head being alive at, and the bias is scaled with them and
 * then **clamped** into `BIAS_ROOM`. What is given up is a threshold the search
 * had put every board on one side of, which is exactly the thing that made the
 * node constant.
 *
 * **Each layer's ordering is preserved and the composition's is not** — the
 * next layer reads inputs that have moved nonlinearly — so this is a rescue
 * rather than an identity, and every caller re-tests it rather than trusting
 * it.
 *
 * Null when there is no scale to borrow: a layer of exact zeros, which is
 * `zeroParams` and every champion that has never been searched. Nought times
 * anything is nought, so that case wants a fresh vector instead.
 */
/**
 * Where output node `n`'s bias sits in the flat vector.
 *
 * Derived from `SHAPE` rather than written down, because the one thing a
 * stored vector cannot tell you is its own layout — and a hand-counted offset
 * is the bug that would silently edit the belt answer while claiming to touch
 * the score.
 */
const outputBiasAt = (node: number): number =>
  paramCount(SHAPE.slice(0, -1)) + node * (SHAPE[SHAPE.length - 2] + 1) + SHAPE[SHAPE.length - 2];

/** How much offset is worth correcting. Below this the edit is churn. */
const OFFSET_EPS = 0.02;

/**
 * Take the constant out of the score answer, and leave everything else.
 *
 * **A constant score cancels, so carrying one is pure waste** — and it is not
 * a small one: measured across real boards, `balanced` ran an offset of 91
 * centipawns against a spread of 96, so half of what the army every ordinary
 * enemy plays contributed was a number that could not move a decision. The
 * offset also *costs* discrimination, because it pushes the output node away
 * from zero where `tanh` is steepest and into the flat part where differences
 * are compressed. Removing it recovers both.
 *
 * **The pre-activation, and on this node that is now the answer.** It reads `z`
 * rather than the output because it always had to — the last layer used to be
 * squashed like the others, so subtracting a constant from the *answer* was not
 * something a bias could do, and re-centring `z` was the nearest honest thing:
 * exactly order-preserving only where `tanh` is linear, which the field's
 * measured operating point happened to be (mean |z| 0.08 to 1.57, saturation 0%
 * for nine of eleven).
 *
 * With the score node linear (`SCORE_OUTPUT`) that caveat is gone: `z` *is* the
 * answer up to `scale` and a clamp, so subtracting its mean subtracts exactly
 * that many centipawns from every board and the ordering is preserved exactly
 * rather than nearly. The one place it can still bite is a board the clamp was
 * holding, which is a head already saturated by any measure.
 *
 * **The score output alone.** A constant *belt* answer does not cancel — it
 * multiplies `w.beltDebt`, which is a finding worth keeping — and a constant
 * depth answer is the mechanism being switched off rather than a waste to
 * tidy. Both are somebody's business; this is not.
 */
export function centreScore(params: readonly number[]): number[] {
  if (params.length !== PARAMS) return [...params];
  let seed = 20260812;
  const next = (): number =>
    (seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0) / 4294967296;

  // The same fixed sample `headResponse` judges on, and for the same reason:
  // this runs in a search that must replay identically, so a die or a clock
  // here would be a generation nobody can reproduce.
  const modes = [MODE_EVALUATE, MODE_SCORE];
  let sum = 0;
  let n = 0;
  for (let i = 0; i < 64; i++) {
    const inputs = probeBoard(next, modes[i % modes.length]);
    // The pre-activation of the score node: the same forward pass `run` makes,
    // stopped one squash short.
    let layer: number[] = INPUTS.map((name) => squash(name, inputs[name]));
    let at = 0;
    for (let l = 1; l < SHAPE.length; l++) {
      const out: number[] = [];
      for (let j = 0; j < SHAPE[l]; j++) {
        let z = 0;
        for (let i2 = 0; i2 < SHAPE[l - 1]; i2++) z += layer[i2] * (params[at++] ?? 0);
        z += params[at++] ?? 0;
        if (l === SHAPE.length - 1 && j === 0) {
          sum += z;
          n++;
        }
        out.push(tanh(z));
      }
      layer = out;
    }
  }
  const offset = n > 0 ? sum / n : 0;
  if (!Number.isFinite(offset) || Math.abs(offset) < OFFSET_EPS) return [...params];
  const out = [...params];
  const at = outputBiasAt(0);
  out[at] = Number((out[at] - offset).toFixed(6));
  return out;
}

export function renormalizeHead(params: readonly number[]): number[] | null {
  if (params.length !== PARAMS) return null;
  const out = [...params];
  let at = 0;
  for (let l = 1; l < SHAPE.length; l++) {
    const fanIn = SHAPE[l - 1];
    const start = at;
    let square = 0;
    let count = 0;
    for (let j = 0; j < SHAPE[l]; j++) {
      for (let i = 0; i < fanIn; i++) {
        square += params[at] * params[at];
        at++;
        count++;
      }
      // The bias is stepped over for the measurement: it is not read off the
      // board, so it says nothing about how big this layer's response to one is.
      at++;
    }
    const rms = Math.sqrt(square / count);
    if (!(rms > 0)) return null;
    const factor = SEED_GAIN / Math.sqrt(fanIn) / rms;
    let write = start;
    for (let j = 0; j < SHAPE[l]; j++) {
      for (let i = 0; i < fanIn; i++) {
        out[write] = Number((out[write] * factor).toFixed(6));
        write++;
      }
      const shifted = out[write] * factor;
      out[write] = Number(
        (Math.sign(shifted) * Math.min(Math.abs(shifted), BIAS_ROOM)).toFixed(6)
      );
      write++;
    }
  }
  return out;
}

/**
 * How small an output row may be, against its layer's own gain, before it
 * counts as dead rather than quiet.
 *
 * **Measured, and the field is bimodal so there is a wide gap to sit in.** Read
 * off the live champions: belt and depth rows are either **at or below 0.01**
 * or **at or above 0.16**, with nothing between, against a layer gain of 0.45.
 * A quarter of the gain is 0.11 — an order of magnitude above the dead cluster
 * and a third below the live one.
 */
const ROW_DEAD = 0.25;

/** Below this a row cannot be rescaled at all, only redrawn. */
const ROW_ZERO = 1e-4;

/**
 * Bring an output row that has gone quiet back to its layer's own scale.
 *
 * **The rescue `renormalizeHead` cannot perform, and the reason is that it
 * scales a whole layer.** A row dead *relative to its neighbours* keeps its
 * relative deadness through any uniform factor — measured across the five
 * stuck champions, a layer renormalise lifted the depth row from 0.0034 to
 * 0.0108 and the depth swing from 0.003 to 0.021, against a threshold of 0.25.
 * Every one of them stayed idle. It is the right operation for a *saturated*
 * head and it is arithmetic that cannot help a *starved* row.
 *
 * What starves one is that nothing selects on it. No goal scores a belt answer
 * or a depth answer — `DIAGNOSTIC_METRICS` forbids the latter outright — so
 * those rows are unrewarded parameters, and any deviation from neutral is a
 * behaviour change the fitness is at best indifferent to. The surviving lineage
 * is the one that drifted least, every generation, until the row is a rounding
 * error.
 *
 * **So this is a bootstrap rather than a cure, and the distinction matters.**
 * It cannot make the search want a live depth answer. What it does is put one
 * candidate in the pool that *has* one, which is the single thing the eviction
 * gate needs: `worthyHead` then holds the pool there, because a bred child that
 * lets the row starve again is unworthy and is dropped. The gate could never
 * open on its own — every head in the pool descended from the same starved one
 * — and once opened it keeps itself open.
 *
 * Direction is preserved wherever there is any left to preserve. A row below
 * `ROW_ZERO` has none, and is redrawn from the deterministic seed at the
 * layer's gain — never a die, because this runs on the read path and inside a
 * migration, and both must give every client the same vector.
 */
export function wakeRows(
  params: readonly number[],
  salt = 0,
  /** Which output rows to consider. Every one of them by default. */
  nodes?: readonly number[]
): number[] {
  if (params.length !== PARAMS) return [...params];
  const out = [...params];
  const fanIn = SHAPE[SHAPE.length - 2];
  const gain = SEED_GAIN / Math.sqrt(fanIn);
  const base = paramCount(SHAPE.slice(0, -1));

  const wanted = nodes ?? Array.from({ length: SHAPE[SHAPE.length - 1] }, (_, i) => i);
  for (const node of wanted) {
    const at = base + node * (fanIn + 1);
    let square = 0;
    for (let i = 0; i < fanIn; i++) square += out[at + i] * out[at + i];
    const rms = Math.sqrt(square / fanIn);
    if (rms >= ROW_DEAD * gain) continue;

    if (rms < ROW_ZERO) {
      // **Nothing to scale, so it is drawn — and not from this row's own seed,
      // which is the trap.** `seedParams` leaves the belt and depth rows
      // *neutral*, deliberately, so that a resized network behaves as it did;
      // reading a replacement out of them fills a row of zeros with zeros.
      // Driven: one champion's head came through this branch unchanged, 0.0000 to
      // 0.0000, and read as the whole rescue failing rather than as the one row
      // it could not reach.
      //
      // So the donor is the *score* row of a differently-salted seed: hashed,
      // deterministic, fan-in scaled, and non-neutral by construction because
      // the score row is the one `seedParams` does draw. Salted by node, or
      // both dead rows would wake as the same numbers.
      const donor = seedGrown(salt + 1 + node);
      for (let i = 0; i <= fanIn; i++) out[at + i] = donor[base + i];
      continue;
    }
    const factor = gain / rms;
    for (let i = 0; i < fanIn; i++) {
      out[at + i] = Number((out[at + i] * factor).toFixed(6));
    }
    // The bias travels with them and is then held inside `BIAS_ROOM`, exactly
    // as `renormalizeHead` does: scaled up with the weights it would be the
    // fastest way to pin the node that was just woken.
    const shifted = out[at + fanIn] * factor;
    out[at + fanIn] = Number(
      (Math.sign(shifted) * Math.min(Math.abs(shifted), BIAS_ROOM)).toFixed(6)
    );
  }
  return out;
}

/**
 * The least intervention that gets a head reading the board again.
 *
 * **Escalating, because the three faults want three different repairs and
 * applying the strongest to all of them is destructive.** A head can be:
 * *starved in one row* (the row's own weights have decayed while its
 * neighbours are fine), *starved throughout* (every layer below the gain), or
 * simply gone. Only the first is common, and it is the only one `wakeRows`
 * alone fixes — but a layer renormalise applied on top of a **working** head
 * throws away the scale a search chose, which is why this stops at the first
 * step that works rather than doing all three.
 *
 * Measured on the live field: `wakeRows` alone wakes three of the five stuck
 * champions and leaves the six healthy ones byte-identical; the remaining two
 * are starved in every layer as well and need the renormalise under it.
 *
 * Deterministic at every step — no die, because this runs on the read path and
 * inside a migration, and both have to hand every client the same vector.
 */
export function repairHead(params: readonly number[], salt = 0): number[] {
  if (params.length !== PARAMS) return [...params];
  const working = (p: number[]): boolean => !isDullHead(p) && !isIdleDepth(p);

  const rungs: number[][] = [];
  // 1. A dead row, brought back to its layer's scale. A no-op on a head whose
  //    rows are all live, which is what keeps this safe to run over everybody.
  rungs.push(wakeRows(params, salt));
  // 2. The whole head under-scaled as well. `renormalizeHead` is the existing
  //    answer to that and cannot fix a row on its own; composed, each covers
  //    what the other cannot.
  const rescaled = renormalizeHead(rungs[0]);
  if (rescaled) rungs.push(wakeRows(rescaled, salt));
  // 3. Nothing left to preserve — a fresh seed on the model's own function,
  //    **with its rows woken.** `seedGrown` leaves belt and depth neutral on
  //    purpose, so a bare re-flash is guaranteed to be idle on depth: driven,
  //    one champion's head fell to this rung and came back 0.000, the last step of
  //    the rescue reliably producing the state it was rescuing from.
  rungs.push(wakeRows(withEcho(seedGrown(salt)), salt));

  for (const rung of rungs) if (working(rung)) return rung;
  // **Never worse than it was**, which a strict ladder cannot promise: the
  // rungs are progressively more destructive, so falling off the end and
  // returning the last one hands back the biggest edit *and* the worst result.
  // Nothing here can wake a head whose middle layers carry no signal — that is
  // not a row fault and no rescaling reaches it — so the honest answer is the
  // liveliest of what was tried, and it may be the original.
  return [params as number[], ...rungs].reduce((best, next) =>
    headResponse(next).depthSwing > headResponse(best).depthSwing ? next : best
  ) as number[];
}

/**
 * The depth row alone, woken.
 *
 * **The belt row is deliberately left neutral and the depth row is not, and the
 * difference is that one of them has a measurement behind it.** A fresh head
 * opens with both at nought "like a seed" — but the belt neutral is load
 * bearing and priced: the headless campaign cleared two fronts at a multiplier
 * of one and *none at all* at 0.77, so a replacement is not the place to
 * discover how sensitive `w.beltDebt` is. Nothing of the kind was ever measured
 * for depth; it was grouped in with its neighbour.
 *
 * And the depth neutral is what the eviction rule condemns. `worthyHead` asks
 * `isIdleDepth`, which is about the third output only — so a head drawn with a
 * flat depth answer is born unworthy and dropped on arrival, which had `revive`
 * and the eviction rule undoing each other every generation at a rate of
 * **forty out of forty draws**. Waking one row settles that and leaves the
 * measured finding alone.
 */
export const wakeDepthRow = (params: readonly number[], salt = 0): number[] =>
  wakeRows(params, salt, [DEPTH_OUTPUT]);

/**
 * A head that is doing nothing, re-flashed. Anything else is left alone.
 *
 * **Deterministic, and that is what lets it run in the game.** Re-noising a
 * dead head with a roll is the tuner's job, where a `roll` is threaded through
 * and reproducible; here the same dead vector must become the same live vector
 * on every client, or two players derive different play from one champion.
 *
 * **Rescale before replace.** A saturated head still holds the direction the
 * search found, and `renormalizeHead` gives it back its slope without giving up
 * that direction; only a head with nothing to rescale, or one still flat after
 * the rescale (its signs cancel rather than its weights saturating), falls
 * through to a fresh vector.
 *
 * `salt` is what stops two armies that both arrive dead being handed the
 * *identical* head. `seedParams` is a fixed hash, so with no salt every fallback
 * in the field is the same fifty-nine numbers — varied here by something the
 * champion already carries rather than by a die, since a die on the read path
 * is a desync. See `saltOf` in `tuning.ts` for what is hashed.
 */
export const reviveHead = (
  params: readonly number[],
  salt = 0,
  /** How much of the linear score the replacement should restate. See `echoFor`. */
  share = ECHO_SHARE,
  /** The authority it will be played at. See `withEcho`. */
  scale = OUTPUT_SCALE
): number[] => {
  if (!isFlatHead(params)) return [...params];
  const rescaled = renormalizeHead(params);
  if (rescaled && !isFlatHead(rescaled)) return rescaled;
  // A refresh, so it opens on the model's own function rather than on noise.
  // The salted seed is the carrier; the echo is what makes it a start rather
  // than a perturbation. See `ECHO_SHARE` for why `seedParams` itself has none.
  return withEcho(seedGrown(salt), share, scale);
};

/**
 * A brand-new head, rolled, for a search that has to replace a dead one.
 *
 * **The tuner's counterpart to `reviveHead`, and it takes a roll on purpose.**
 * The read path must hand every client the same vector; a *search* wants a
 * different one each time it reaches for one, or `revive` spends a candidate's
 * long jump putting the same fifty-nine numbers back. The roll is the island's,
 * so it varies by island and advances with the generation.
 *
 * **Fan-in scaled, biases at nought, and the belt and depth rows left neutral**
 * — every one of those is `seedParams`' argument, which applies unchanged to a
 * replacement. What is different is only that the draw is rolled rather than
 * hashed. Measured over two hundred draws: at this gain the median head answers
 * with a spread of 16 centipawns and pins nought percent of boards at the rail,
 * against `revive`'s old flat ±3 at a median of 74 with **56% pinned** — which
 * is not a responsive head, it is a switch.
 *
 * **Retried while dull, and the attempt count is what makes the retry mean
 * something** — the same trick `looseTurn` uses. Each attempt draws further
 * down the same stream, so a second try is genuinely a second head, and the
 * fallback is the hashed seed, which is known live. Nearly unreachable now that
 * the echo goes on last: a draw would have to cancel it. Kept because "nearly"
 * is not a guarantee and the guard costs one flatness probe.
 */
export function freshHead(
  roll: () => number,
  tries = 4,
  /** How much of the linear score to open by restating. See `echoFor`. */
  share = ECHO_SHARE,
  /** The authority it will be played at. See `withEcho`. */
  scale = OUTPUT_SCALE
): number[] {
  for (let attempt = 0; attempt < tries; attempt++) {
    const out: number[] = [];
    for (let l = 1; l < SHAPE.length; l++) {
      const spread = SEED_GAIN / Math.sqrt(SHAPE[l - 1]);
      for (let j = 0; j < SHAPE[l]; j++) {
        const neutral = l === SHAPE.length - 1 && (j === BELT_OUTPUT || j === DEPTH_OUTPUT);
        for (let i = 0; i < SHAPE[l - 1]; i++) {
          out.push(neutral ? 0 : Number(((roll() * 2 - 1) * spread).toFixed(6)));
        }
        out.push(0);
      }
    }
    // The echo goes on last, over the draw — a replacement starts by agreeing
    // with the model it is bolted to, exactly as a seed does. See `ECHO_SHARE`.
    withEcho(out, share, scale);
    // **Its depth row woken, or it is born condemned.** The rows open neutral
    // just below, which is right for belt and fatal for depth: `worthyHead`
    // asks `isIdleDepth`, so a flat third output is an unworthy candidate the
    // moment it is drawn. Measured before this: forty of forty draws came out
    // idle, and `revive` — which runs on half the generation — was handing the
    // eviction rule something to throw away every time. See `wakeDepthRow`.
    const woken = wakeDepthRow(out, Math.floor(roll() * 1e9));
    // **Redrawn until the draw is one that works, which is what the loop was
    // already for.** A woken depth row clears `isIdleDepth` on about 55% of
    // draws — measured over two hundred, median swing 0.285 — so this is the
    // salt rather than the magnitude, and scaling the row to force it is
    // fitting a threshold: the swing *peaks* near three times the layer gain
    // (0.267) and falls away past it as the node saturates.
    //
    // Why it has to clear at all: `revive` runs on half of every generation and
    // `worthyHead` asks `isIdleDepth`, so a draw that fails is a candidate born
    // condemned. Before this, forty of forty were.
    if (!isDullHead(woken) && !isIdleDepth(woken)) return woken;
  }
  // The last resort takes the waking too, or the one draw that is guaranteed
  // to happen when every other has failed is the one guaranteed to be idle.
  return wakeDepthRow(withEcho(seedGrown()), 0);
}

/** A head that does nothing at all. The identity, for tests and for proofs. */
export const zeroParams = (): number[] => new Array(PARAMS).fill(0);

/** Where one parameter lives: layer (1-based), node in it, input to it. */
function slotIndex(layer: number, node: number, input: number): number {
  let at = 0;
  for (let l = 1; l < layer; l++) at += SHAPE[l] * (SHAPE[l - 1] + 1);
  return at + node * (SHAPE[layer - 1] + 1) + input;
}

/**
 * How much of the linear score a fresh head restates.
 *
 * **A new head starts by agreeing with the model it is attached to, rather than
 * by perturbing it at random.** A hashed or rolled vector is a ±16 centipawn
 * noise field laid over a tuned evaluation: every parameter has slope, and the
 * slope points nowhere in particular, so the first thing the search must do is
 * beat its own initialisation back out. Seeded to echo `linear` — the estimate
 * so far, which the head already takes as its eighth input — it starts on the
 * one function the search has already established is worth something.
 *
 * **The total stays a monotone transform of the score, which is what makes this
 * free.** `linear + 200·tanh(…linear…)` rises wherever `linear` rises, so board
 * A beats board B before and after; an evaluation is only ever compared with
 * another evaluation, so a monotone rescaling changes no decision. Verified
 * across ±3000 at every gain tried. It compresses at the tails, three nested
 * `tanh` deep — 0.15 of the score at 200 centipawns and about a tenth of it at
 * three thousand — and that is still monotone, so it costs nothing but reach.
 *
 * **What is *not* done is the other half of the idea: taking the echo back out
 * of the linear coefficients.** Two reasons, and the first is that there is
 * nothing to take back — the echo is a monotone transform of the whole score
 * rather than a change to what one subtotal is worth against another, so no
 * relative price has moved and there is no compensation to make. The second is
 * what it would cost: `evaluate` accumulates its six subtotals from some
 * seventy named weights and there is no table saying which weight feeds which,
 * on purpose — `WEIGHT_GROUPS` partitions by what a weight is *about*, which
 * this file's own header notes is a different bucketing. Writing one would be a
 * second copy of the evaluation's structure, maintained by hand, going stale
 * the first time somebody adds a term.
 *
 * Approximate rather than exact, and worth saying plainly twice over: the other
 * twelve inputs still carry their noise, so a refreshed head is the echo *plus*
 * a perturbation; and "changes no decision" is true of comparisons between
 * evaluations, which is nearly all of them, and not of the handful of places a
 * score meets an absolute number — `noise`'s centipawn band, `mintRestraint`'s
 * bar. Those see a score up to a seventh larger.
 *
 * **A refresh only, never `seedParams` itself.** `WEIGHTS.perceptron` *is* a
 * seed, so putting the echo there would re-price the baseline every army starts
 * from and every stored score was measured against — the ruler moving under the
 * field, for a change nobody has played a game to justify. A refresh is
 * different in kind: the vector it replaces has already been measured and has
 * already failed, so there is nothing to preserve and everything to gain from
 * starting it somewhere the search knows is worth something. Driven, when this
 * was scoped wrongly: three unrelated tests measuring 900-centipawn effects
 * against 45-centipawn tolerances went red at once.
 */
export const ECHO_SHARE = 0.15;

/**
 * Lay the echo path over a drawn head: `linear` → node 0 → node 0 → the score.
 *
 * One path reserved, the rest of the vector left as it was drawn. Three weights
 * whose product is what turns a squashed input back into a share of a score:
 * the input arrives divided by `INPUT_SCALE` and the output is multiplied by
 * `OUTPUT_SCALE`, so the gain each has to carry is the cube root of what is
 * left. The search can dismantle it — every one of the three is an ordinary
 * parameter — which is the point: this is where it starts, not where it stays.
 */
/**
 * The board the echo is sized on: a middling score.
 *
 * The measured median |linear| over a played game, which is the same number
 * `headRange` and `LINEAR_SCALE` are sized against. A share is only a share
 * *somewhere* — three nested squashes make it fall with the size of the input —
 * so the honest thing is to name the board it is exact on.
 */
const ECHO_PROBE = 628;

/**
 * How much of the linear score a head should restate at a given rung.
 *
 * **At a rung, parity is the whole point.** `blended` computes
 * `linear * (1 - blend) + head`, so a head restating exactly `blend` of the
 * linear sum makes the total *equal* to what the linear model alone said — the
 * rung opens on level ground and the search improves from there. Anything less
 * and every rung starts by handing back evaluation the army had already earned,
 * which is the trough that makes a ramp refuse to be walked.
 *
 * At rung nought there is nothing to match, so it keeps `ECHO_SHARE`: the head
 * is a correction there, and opening as a small restatement of the model rather
 * than as noise is that constant's own argument.
 */
export const echoFor = (blend: number): number => (blend > 0 ? blend : ECHO_SHARE);

/**
 * Lay the echo path over a drawn head: `linear` → node 0 → node 0 → the score.
 *
 * One path reserved, the rest of the vector left as it was drawn. The search can
 * dismantle it — every weight on it is ordinary — which is the point: this is
 * where it starts, not where it stays.
 *
 * **The gain is solved for, not derived, and that is a repair.** It used to be
 * `cbrt(share · INPUT_SCALE / OUTPUT_SCALE)`, which is the right arithmetic for
 * exactly one arrangement: `linear` arriving divided by `INPUT_SCALE` and the
 * score node squashing on the way out. Both of those changed underneath it — v29
 * gave `linear` its own `LINEAR_SCALE` and v30 made the output linear — and the
 * formula did not, so a fresh head restated **4.5–9.2%** of the linear sum while
 * `ECHO_SHARE` said 15%. Nothing failed; the on-ramp was just half the height it
 * claimed, which is invisible until somebody tries to walk up it.
 *
 * Bisected instead: write a gain, measure what the head actually restates, and
 * narrow. That costs a few dozen forward passes at seed time — the same order as
 * the flatness probe `reviveHead` already runs — and buys a property that is
 * *measured* rather than asserted, so the next change to a transform or an
 * activation cannot quietly halve it again.
 *
 * `scale` is the authority the head will be played at, which at a rung is not
 * `OUTPUT_SCALE`: `blended` runs it from `w.head` to `w.headRange` as the blend
 * opens, and a gain solved against the wrong one is off by that whole ratio.
 */
function withEcho(
  params: number[],
  share = ECHO_SHARE,
  scale = OUTPUT_SCALE
): number[] {
  const slots = [slotIndex(1, 0, INPUTS.indexOf("linear"))];
  for (let l = 2; l < SHAPE.length; l++) slots.push(slotIndex(l, 0, 0));

  const probe = noInputs();
  probe.mode = MODE_EVALUATE;
  // A board rather than an isolated input: `linear` is the six subtotals added
  // up, so a probe where it disagrees with them is one the head never sees.
  probe.linear = ECHO_PROBE;
  probe.material = ECHO_PROBE;

  const restates = (gain: number): number => {
    for (const slot of slots) params[slot] = gain;
    return run(params, probe, scale).score / ECHO_PROBE;
  };

  // Monotone in the gain along a path of one sign, so bisection is enough — and
  // unlike a closed form it degrades gracefully: a share the path cannot reach
  // lands at the top of the bracket rather than at a wrong number.
  let lo = 0;
  let hi = 8;
  if (restates(hi) > share) {
    for (let step = 0; step < 40; step++) {
      const mid = (lo + hi) / 2;
      if (restates(mid) < share) lo = mid;
      else hi = mid;
    }
  }
  const gain = Number(hi.toFixed(6));
  for (const slot of slots) params[slot] = gain;
  return params;
}

/**
 * A fresh head: what anything with no stored vector starts from.
 *
 * **Weights scaled by fan-in, biases at nought.** Three failures it is written
 * around, every one of them measured rather than argued:
 *
 * - **A flat fill is a tilt.** Every parameter positive means every sum is
 *   positive means the head is a standing bonus on every board it sees. With
 *   one, the headless campaign went from clearing two fronts to one.
 * - **A small fill never reaches the output.** At 0.05 the signal is three
 *   layers of multiplication down and the answer is a constant — flat by this
 *   file's own test. `1/sqrt(fan_in)` is where it comes alive.
 * - **A seeded bias is a tilt directly**, since a bias is exactly the part of a
 *   node that does not depend on the board. So they start at zero, and the
 *   whole of a fresh head's output is a response to the position.
 *
 * Deliberately **not** the textbook symmetry argument, which does not apply
 * here: identical weights stay identical under *gradient descent*, and this is
 * an evolutionary search whose `mutate` maps every element of an array through
 * its own roll — a constant would come apart on the first generation that
 * touched it. The tilt is the real cost, and that one does not come apart,
 * because mutation is multiplicative and a scaled positive is still positive.
 *
 * Deterministic, never rolled. `src/engine/ai/` may not read a clock or a die — two
 * clients derive one game from one log — so the spread is a fixed hash of the
 * slot: varied, reproducible, and near enough zero-mean.
 *
 * `salt` mixes into that hash, and exists because a fixed hash is the *same*
 * fifty-nine numbers for everybody: two armies falling back here both get one
 * head, so an evaluation meant to be a style's own is shared. A salt off
 * something the caller already has keeps it deterministic and stops it being a
 * constant. **Nought reproduces the original vector exactly** — `x ^ 0` is `x` —
 * so nothing that already stores a seeded head has moved.
 */
export function seedParams(salt = 0, shape: readonly number[] = SHAPE): number[] {
  const out: number[] = [];
  for (let l = 1; l < shape.length; l++) {
    // A node summing six inputs wants smaller weights than one summing two, or
    // the wide layer saturates `tanh` while the narrow one barely moves it.
    const scale = SEED_GAIN / Math.sqrt(shape[l - 1]);
    for (let j = 0; j < shape[l]; j++) {
      // **The belt output seeds flat, and the score output does not.** The two
      // have different neutrals: a score of zero is "no opinion", so a seeded
      // slope there costs nothing and buys the search a foothold — while the
      // belt answer is a *multiplier*, whose neutral is one, and one is what a
      // zero output exponentiates to. Seeded like its neighbour it starts at
      // 0.77, which is not "no opinion" but "lay a quarter less track than the
      // style asked for", from a hash rather than from a finding.
      //
      // Measured, not argued: the headless campaign cleared two fronts at a
      // multiplier of one and **none at all** at 0.77. `beltDebt` is a far
      // more sensitive number than it looks, and a seed is not the place to
      // discover that. `mutate` and `jitter` both move a zero additively, so
      // the search reaches it on the first generation regardless.
      // **Both of the non-score outputs seed flat, and for one reason.** A
      // score of zero is "no opinion", so a seeded slope there costs nothing
      // and buys the search a foothold. The other two have neutrals that are
      // not zero-the-number but zero-the-*meaning*: the belt answer is a
      // multiplier whose neutral is one, and the depth answer is an offset
      // whose neutral is the ordinary depth. Seeded like their neighbour they
      // would open by laying a quarter less track and thinking a turn less —
      // from a hash rather than from a finding, and paid in wall clock.
      const neutral =
        l === shape.length - 1 && (j === BELT_OUTPUT || j === DEPTH_OUTPUT);
      for (let i = 0; i < shape[l - 1]; i++) {
        if (neutral) {
          out.push(0);
          continue;
        }
        // A cheap integer hash, so neighbouring slots do not land on
        // neighbouring values and the pattern carries no structure a search
        // could mistake for signal.
        const h = Math.imul((out.length + 1) ^ salt, 2654435761) >>> 0;
        out.push(Number((((h / 4294967296) * 2 - 1) * scale).toFixed(6)));
      }
      out.push(0);
    }
  }
  return out;
}

/**
 * The shape the shipped default head was authored on. **Never follows `SHAPE`.**
 *
 * `seedParams` hashes each weight off its *position in the vector* and scales it
 * by the layer's fan-in, so both halves of it move when the shape does: a grow
 * renumbers every slot after the first layer and re-scales the first. The seed
 * is therefore not one head that grows — it is a different head per shape, and
 * `WEIGHTS.perceptron` reading it live meant **every reshape silently re-rolled
 * the evaluation the game ships with**.
 *
 * Which is not a small thing dressed up. `REFERENCE` is `WEIGHTS`, so it is the
 * ruler every champion's score is taken against and the baseline every ratio in
 * `styleWeights` is expressed against; and any profile without a champion plays
 * it. Measured when the input layer went 13 -> 17: two `greed` assertions
 * flipped with the four new sensors *disabled*, and came back the moment the
 * head was zeroed — the sensors were innocent and the re-roll was the whole
 * effect. That was read at the time as the new inputs moving the planner.
 */
export const SEED_SHAPE: readonly number[] = [13, 8, 5, 3];

/**
 * The shipped default head: the frozen seed, grown onto whatever `SHAPE` is now.
 *
 * **It migrates exactly as a champion does**, which is the property that was
 * missing — `resizeParams` puts every authored weight in the slot it always
 * read and pads the new ones with nought, so a reshape leaves the default
 * computing precisely what it computed and the new inputs contribute nothing
 * until a search finds them. A grow is a re-measurement rather than a retrain
 * for the baseline on the same terms it already is for the field.
 *
 * A *shrink* or a change in layer count refuses, and then the honest answer is
 * the re-roll: the old vector's weights no longer read the inputs they were
 * chosen against, which is the same reading `resizeParams` gives everywhere.
 */
export const seedGrown = (salt = 0): number[] =>
  resizeParams(seedParams(salt, SEED_SHAPE), SEED_SHAPE) ?? seedParams(salt);

export const defaultParams = (): number[] => seedGrown(0);

/**
 * Move a stored network onto a new shape, or refuse.
 *
 * **Growing resizes cleanly; shrinking demands a complete retrain.** The
 * asymmetry is not a convenience, it is what the two directions actually mean:
 *
 * - **Grown**, every parameter the old network had still means what it meant —
 *   the same input in the same slot, the same node reading it — and the new
 *   ones are padded with **zero**, which contributes nothing to any sum. So the
 *   resized network computes *exactly* what it computed before, to the last
 *   bit, and the search continues from a vector it has already paid for rather
 *   than from scratch. That is the whole of "cleanly": not merely automatic,
 *   but behaviour-preserving, which is the only version worth having, since a
 *   resize that quietly changed the evaluation would invalidate the champion it
 *   was trying to save.
 * - **Shrunk**, there is no such story. Dropping a node discards the weights
 *   feeding it *and* the weights it fed, so every remaining parameter is now
 *   reading a different function than the one it was tuned against — the
 *   numbers survive and their meaning does not. Rescaling the survivors is the
 *   tempting fix and it is worse than useless: it produces a vector that looks
 *   trained, scores plausibly, and is a stranger to every board it was searched
 *   on. So this returns null and the caller starts from `zeroParams`, which
 *   costs a search and never lies.
 *
 * Layer count is part of the shape: four layers to three is a shrink whatever
 * the widths do.
 */
export function resizeParams(
  stored: readonly number[],
  from: readonly number[],
  to: readonly number[] = SHAPE
): number[] | null {
  if (stored.length !== paramCount(from)) return null;
  if (to.length !== from.length) return null;
  // Narrower anywhere is a retrain, even if it is wider somewhere else: a
  // network that lost a node and gained one elsewhere has not been resized, it
  // has been replaced.
  for (let i = 0; i < to.length; i++) if (to[i] < from[i]) return null;
  if (to.every((n, i) => n === from[i])) return [...stored];

  const out = new Array(paramCount(to)).fill(0);
  let read = 0;
  let write = 0;
  for (let l = 1; l < to.length; l++) {
    for (let j = 0; j < to[l]; j++) {
      for (let i = 0; i < to[l - 1]; i++) {
        // A weight the old network had keeps its slot; everything the growth
        // added stays zero, so the added nodes contribute nothing and the added
        // inputs are ignored until the search finds a use for them.
        const had = j < from[l] && i < from[l - 1];
        out[write++] = had ? stored[read++] : 0;
      }
      out[write++] = j < from[l] ? stored[read++] : 0;
    }
  }
  return out;
}
